import { Annotation, AnnotoriousAnnotation } from '@App/app/entities/files/annotation.model';
import { AnnotationForm } from '@App/app/entities/forms/annotation-form.model';
import { Coordinates2 } from '@App/app/entities/layer/measurements/coordinates';
import { AnnotationPriorities } from '@App/app/entities/shared/annotation-priorities.enum';
import { getMinMaxFromSelector } from '@App/app/shared/utils/annotations.utils';
import { Directive, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NbToastrService } from '@nebular/theme';
import { Annotorious } from '@recogito/annotorious';
import { takeWhile } from 'rxjs/operators';
import { Annotations2dService } from './services/annotations2d-service/annotations2d.service';

/**
 * Holds the Annotorious instance, its event handlers and the editing popup/form logic.
 * The component extends this and wires up the Angular lifecycle and template bindings.
 */
@Directive()
export abstract class ModelPhotoCardZoomAnnotationsBaseDirective {
  @ViewChild('editor') editor: ElementRef<HTMLDivElement> | null;
  @Input() annotations: Annotation[];
  @Input() htmlImage: HTMLImageElement;
  @Input() htmlWrapper: HTMLDivElement;
  @Input() parentRef: HTMLElement;
  @Input() visible: boolean;
  @Input() editingActive: boolean;
  @Output() save = new EventEmitter();
  protected initialized = false;
  protected annotorious: Annotorious;
  selectedAnnotation: Annotation | null = null;
  editingForm: FormGroup | null = null;

  constructor(
    protected fb: FormBuilder,
    protected annotations2dService: Annotations2dService,
    protected toastrService: NbToastrService,
  ) {}

  isSelectedAnnotationSaved() {
    return (
      this.selectedAnnotation &&
      this.annotations.some((a) => a.localId === this.selectedAnnotation?.localId)
    );
  }

  updateAnnotationsUI = () => {
    const annotations = this.collectAnnotationsForUI();
    const annotoriousAnnotations = this.annotorious.getAnnotations() as AnnotoriousAnnotation[];
    const viewBox = this.parentRef.querySelector('.a9s-annotationlayer');
    for (const ann of annotations) {
      const annotoriousAnn = annotoriousAnnotations.find(
        (a) => a.id === ann.localId || (ann.localId === 'undefined' && !a.id),
      );
      if (annotoriousAnn) {
        ann.updateUI(viewBox, this.htmlImage);
      }
    }
  };

  private collectAnnotationsForUI(): Annotation[] {
    const annotations = [...this.annotations];
    if (this.selectedAnnotation && this.editingActive) {
      const tmpAnnotation = this.selectedAnnotation.copy();
      tmpAnnotation.priority = this.getChanges().priority;
      if (!this.isSelectedAnnotationSaved()) {
        tmpAnnotation.localId = 'undefined';
      }
      annotations.push(tmpAnnotation);
    }
    return annotations;
  }

  protected selectAnnotation = ({ id }: AnnotoriousAnnotation) => {
    const annotation = this.annotations.find((ann) => ann.localId === id);
    if (annotation) {
      this.selectedAnnotation = annotation.copy();
      this.openPopup();
    }
  };

  protected createAnnotation = ({ id }: AnnotoriousAnnotation) => {
    if (this.selectedAnnotation) {
      const { min: mn, max: mx } = this.selectedAnnotation;
      const { title: t, description: d, priority: p } = this.getChanges();
      const annotation = this.annotations2dService.createAnnotation(mn, mx, t, d, p, id);
      this.annotations = this.annotations || [];
      this.annotations.push(annotation);
      this.closePopup();
      this.save.emit();
    }
  };

  protected updateAnnotation = ({ id }: AnnotoriousAnnotation) => {
    if (this.selectedAnnotation) {
      const { min, max } = this.selectedAnnotation;
      const { title, description, priority } = this.getChanges();
      const annotation = this.annotations.find((ann) => ann.localId === id);
      if (annotation) {
        annotation.update(min, max, title, description, priority);
        this.closePopup();
        this.save.emit();
      }
    }
  };

  protected resizeAnnotation = (target: AnnotoriousAnnotation['target']) => {
    const selector = target.selector.value;
    const [min, max] = getMinMaxFromSelector(selector, this.htmlImage);
    if (this.selectedAnnotation) {
      this.selectedAnnotation.min = min as Coordinates2;
      this.selectedAnnotation.max = max as Coordinates2;
    }
  };

  protected deleteAnnotation = ({ id }: AnnotoriousAnnotation) => {
    const annotation = this.annotations.find((ann) => ann.localId === id);
    const index = this.annotations.indexOf(annotation as Annotation);
    if (index !== null) {
      this.annotations.splice(index, 1);
    }
    this.closePopup();
    this.save.emit();
  };

  protected createSelection = ({ target }: AnnotoriousAnnotation) => {
    const { value: selector } = target.selector;
    const [min, max] = getMinMaxFromSelector(selector, this.htmlImage);
    this.selectedAnnotation = this.annotations2dService.createAnnotation(min, max);
    this.openPopup();
  };

  protected cancelSelection = () => {
    this.closePopup();
  };

  protected openPopup() {
    if (this.selectedAnnotation) {
      const annotoriousEditor = this.parentRef.querySelector('.r6o-editor');
      if (this.editingActive) {
        this.initForm();
      }
      this.annotations2dService.bindAnnotoriousControls(annotoriousEditor, this.editingActive);
      if (annotoriousEditor) {
        setTimeout(() => {
          if (this.editor) {
            this.annotations2dService.setEditorStyles(
              annotoriousEditor as HTMLElement,
              this.editor.nativeElement,
              this.parentRef,
            );
          }
        });
      }
      this.updateAnnotationsUI();
    }
  }

  protected closePopup() {
    this.selectedAnnotation = null;
    this.editingForm = null;
    this.updateAnnotationsUI();
  }

  protected getChanges() {
    const form = this.editingForm as FormGroup;
    const title = form.controls.title.value as string;
    const description = form.controls.description.value as string;
    const priority = form.controls.priority.value as AnnotationPriorities;
    return { title, description, priority };
  }

  protected initForm() {
    const ann = this.selectedAnnotation as Annotation;
    this.editingForm = this.fb.group<AnnotationForm>({
      title: ann.title,
      description: ann.description,
      priority: ann.priority,
    });
    this.editingForm.controls.priority.valueChanges
      .pipe(takeWhile(() => this.editingActive))
      .subscribe(() => this.updateAnnotationsUI());
  }
}
